import { useState, useEffect, useCallback } from "react";
import { ExternalLink } from "lucide-react";
import { api, formatPrice } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";

const CATEGORY_IDS = ["hepsi", "kadin", "erkek", "cocuk", "sevgili", "anne", "baba", "arkadas", "is_arkadasi", "yeni_ev", "dugun", "bebek", "mezuniyet"];

const PRICE_RANGES = [
  { id: "all", key: "all_prices", min: null, max: null },
  { id: "p1", key: "pr_p1", min: 0, max: 1000 },
  { id: "p2", key: "pr_p2", min: 1000, max: 2500 },
  { id: "p3", key: "pr_p3", min: 2500, max: 5000 },
  { id: "p4", key: "pr_p4", min: 5000, max: 10000 },
  { id: "p5", key: "pr_p5", min: 10000, max: null },
];

export default function Explore() {
  const { t } = useLang();
  const [category, setCategory] = useState("hepsi");
  const [priceRange, setPriceRange] = useState("all");
  const [products, setProducts] = useState(null);

  const load = useCallback(async () => {
    const pr = PRICE_RANGES.find((p) => p.id === priceRange);
    const params = { category };
    if (pr.min !== null) params.price_min = pr.min;
    if (pr.max !== null) params.price_max = pr.max;
    try {
      const res = await api.get("/explore", { params });
      setProducts(res.data);
    } catch (err) {
      console.error("explore load failed", err);
    }
  }, [category, priceRange]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="explore-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-1">{t("explore_title")}</h1>
      <p className="text-muted-foreground text-sm mb-6">{t("explore_sub")}</p>

      <div className="flex gap-2 overflow-x-auto pb-2 mb-3">
        {CATEGORY_IDS.map((id) => (
          <button key={id} onClick={() => setCategory(id)} data-testid={`explore-cat-${id}`}
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              category === id ? "bg-primary text-white" : "bg-secondary text-foreground hover:bg-secondary/70"}`}>
            {t(`cat_${id}`)}
          </button>
        ))}
      </div>
      <div className="flex gap-2 overflow-x-auto pb-2 mb-6">
        {PRICE_RANGES.map((p) => (
          <button key={p.id} onClick={() => setPriceRange(p.id)} data-testid={`explore-price-${p.id}`}
            className={`shrink-0 px-4 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              priceRange === p.id ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground"}`}>
            {t(p.key)}
          </button>
        ))}
      </div>

      {!products ? (
        <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : products.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">{t("no_products")}</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {products.map((p, i) => (
            <div key={p.product_id} className="bg-card rounded-2xl border border-border/50 overflow-hidden card-lift fade-up"
              style={{ animationDelay: `${i * 0.03}s` }} data-testid={`explore-product-${p.product_id}`}>
              <img src={p.image_url} alt={p.name} className="aspect-square w-full object-cover" />
              <div className="p-3">
                <p className="text-sm font-semibold truncate">{p.name}</p>
                <p className="text-xs text-muted-foreground">{p.store}</p>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-sm text-primary font-bold">{formatPrice(p.price)}</p>
                  <Button size="sm" variant="outline" className="rounded-full h-7 text-xs"
                    onClick={() => window.open(p.product_url, "_blank")} data-testid={`explore-buy-${p.product_id}`}>
                    <ExternalLink size={11} className="mr-1" /> {t("inspect")}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
